import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import sampleReport from "@/assets/Falco_Sample_Dashboard_Report.pdf";
import { toast } from "sonner";
import axios from "axios";
import { 
  Zap, 
  Timer, 
  FileText, 
  BarChart3, 
  XCircle, 
  CheckSquare,
  Rocket,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Lock,
  ExternalLink,
  RefreshCw,
  BookOpen
} from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";



const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [demoAnswer, setDemoAnswer] = useState("");
  const [demoResult, setDemoResult] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const totalSteps = 4;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    }
  };

  const handleSkip = async () => {
    await completeOnboarding();
  };

  const handleFinish = async () => {
    await completeOnboarding();
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/users/me/onboarding-complete`);
      setUser({ ...user, onboarding_completed: true });
      toast.success("Welcome to ExamReady!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Error completing onboarding:", error);
      setUser({ ...user, onboarding_completed: true });
      navigate("/dashboard", { replace: true });
    }
  };

  const handleDemoEvaluate = async () => {
    if (!demoAnswer.trim()) {
      toast.error("Please write an answer first");
      return;
    }

    setIsEvaluating(true);

    try {
      const response = await axios.post(`${API}/onboarding/demo-evaluate`, {
        student_answer: demoAnswer
      });

      const result = response.data;

      setDemoResult({
        score: result.marks_awarded ?? 0,
        total: 2,
        points: result.points_credited?.length > 0 ? result.points_credited : ["No scoreable points found"],
        missed: result.missed_points || [],
        feedback: result.feedback || "No feedback returned."
      });
    } catch (err) {
      console.error("Demo evaluation error:", err);
      toast.error("Evaluation failed. Please try again.");
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleSampleDownload = () => {
    const link = document.createElement("a");
    link.href = sampleReport;
    link.download = "Falco-ExamReady-Sample-Dashboard-Report.pdf";
    link.click();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Progress Dots */}
        <div className="flex justify-center gap-2 pt-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === step ? "bg-[#C9A227] w-6" : i < step ? "bg-[#2E3A59]" : "bg-slate-200"
              }`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>

        <div className="p-8">
          {/* Step 1: Welcome — unchanged */}
          {step === 0 && (
            <div className="animate-fade-in" data-testid="onboarding-step-0">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FDF8E6] rounded-2xl mb-4">
                  <span className="text-3xl">👋</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#2E3A59] mb-2">
                  Welcome to ExamReady!
                </h2>
                <p className="text-slate-600">
                  Your RGUHS MBBS exam practice partner. Get <strong>examiner-like feedback</strong> on every answer.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-[#E8EAF0] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-[#2E3A59]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Single Question</h4>
                    <p className="text-sm text-slate-500">Attempt one at a time. Immediate feedback after submission.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-[#E8EAF0] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Timer className="w-5 h-5 text-[#2E3A59]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Sprint Mode</h4>
                    <p className="text-sm text-slate-500">Batch of 3–5 questions. Evaluate all after completing the sprint.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-[#E8EAF0] rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#2E3A59]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Full Exam Mode</h4>
                    <p className="text-sm text-slate-500">Simulate the real RGUHS paper. 3 hours, full paper pattern.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: How Scoring Works — unchanged */}
          {step === 1 && (
            <div className="animate-fade-in" data-testid="onboarding-step-1">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FDF8E6] rounded-2xl mb-4">
                  <span className="text-3xl">🎯</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#2E3A59] mb-2">
                  How Scoring Works
                </h2>
                <p className="text-slate-600">
                  Finally know exactly what earns marks. Every answer evaluated against a <strong>marking key</strong> built on RGUHS examiner-like expectations.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Marks Breakdown</h4>
                    <p className="text-sm text-slate-500">Content, Structure, Keywords, Diagram, Presentation</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Reason Codes</h4>
                    <p className="text-sm text-slate-500">Exactly why marks were deducted — no guessing</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckSquare className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2E3A59]">Next Attempt Checklist</h4>
                    <p className="text-sm text-slate-500">3–6 specific things to include next time</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Demo — unchanged */}
          {step === 2 && (
            <div className="animate-fade-in" data-testid="onboarding-step-2">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FDF8E6] rounded-2xl mb-4">
                  <span className="text-3xl">✏️</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-[#2E3A59] mb-2">
                  Try it — Right Now
                </h2>
                <p className="text-slate-600">
                  Answer this real RGUHS Biochemistry question and see how evaluation works.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 mb-4">
                <div className="flex items-start justify-between mb-3">
                  <p className="font-heading text-[#2E3A59] leading-relaxed">
                    Define Km. What is its biochemical significance?
                  </p>
                  <span className="text-xs font-bold text-[#C9A227] bg-[#FDF8E6] px-2 py-1 rounded-full whitespace-nowrap ml-3">
                    2 marks
                  </span>
                </div>
                <Textarea
                  value={demoAnswer}
                  onChange={(e) => setDemoAnswer(e.target.value)}
                  placeholder="Type your answer here — even a rough attempt works…"
                  className="min-h-[100px] bg-white border-slate-200 focus:border-[#2E3A59] resize-none"
                  data-testid="onboarding-demo-answer"
                />
                <Button
                  onClick={handleDemoEvaluate}
                  disabled={isEvaluating}
                  className="mt-3 bg-[#2E3A59] hover:bg-[#1A2236] btn-press"
                  data-testid="onboarding-demo-check-btn"
                >
                  {isEvaluating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Evaluating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Check My Answer
                    </>
                  )}
                </Button>
              </div>

              {demoResult && (
                <div className="border rounded-xl overflow-hidden animate-fade-in" data-testid="onboarding-demo-result">
                  <div className={`flex items-center justify-between px-4 py-3 ${
                    demoResult.score >= 1.5 ? "bg-emerald-600" :
                    demoResult.score >= 1 ? "bg-amber-500" : "bg-red-500"
                  }`}>
                    <span className="font-bold text-white text-sm">AI Evaluation Result</span>
                    <span className="text-white font-bold text-lg">{demoResult.score} / {demoResult.total}</span>
                  </div>

                  <div className="bg-white p-4 space-y-3">
                    {demoResult.points.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">✅ Credited</p>
                        <ul className="space-y-1">
                          {demoResult.points.map((p, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                              <span className="text-emerald-500 mt-0.5">•</span>{p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {demoResult.missed?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">❌ Missed</p>
                        <ul className="space-y-1">
                          {demoResult.missed.map((p, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="text-red-400 mt-0.5">•</span>{p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Examiner Feedback</p>
                      <p className="text-sm text-slate-700">{demoResult.feedback}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: CHANGED — quota language, no credits mention ── */}
          {step === 3 && (
            <div className="animate-fade-in" data-testid="onboarding-step-3">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FDF8E6] rounded-2xl mb-4">
                  <span className="text-3xl">🚀</span>
                </div>
                {/* CHANGED: was "60 Monthly Credits — Free Forever" */}
                <h2 className="font-heading text-2xl font-bold text-[#2E3A59] mb-2">
                  Free every 30 days, for each course
                </h2>
                <p className="text-slate-600">
                  No card needed. Here's what you get on the free plan:
                </p>
              </div>

              <div className="space-y-3 mb-5">
                {/* CHANGED: question-type limits, no credit numbers */}
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-emerald-700 text-sm">
                    4 short answers
                  </span>
                </div>

                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-emerald-700 text-sm">
                    2 short essays
                  </span>
                </div>

                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <CheckSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-emerald-700 text-sm">
                    1 long essay
                  </span>
                </div>

                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <RefreshCw className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-medium text-emerald-700 text-sm">
                    Resets every 30 days
                  </span>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-100 border border-slate-200 rounded-xl">
                  <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="font-medium text-slate-500 text-sm">
                    Dashboard &amp; Weak Topic Analysis 🔒 — paid plan
                  </span>
                </div>
              </div>

              {/* View sample PDF link */}
              <div className="text-center mb-4">
                <button
                  onClick={handleSampleDownload}
                  className="inline-flex items-center gap-1.5 text-sm text-[#2E3A59] font-medium hover:underline underline-offset-2 transition-colors"
                  data-testid="onboarding-sample-pdf-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View sample Dashboard Report &amp; Weak Topic Analysis
                </button>
              </div>

              {/* CHANGED: was "60 credits loaded..." */}
              <p className="text-xs text-slate-500 text-center">
                Every answer evaluated against the actual RGUHS marking scheme.
              </p>
            </div>
          )}

          {/* Navigation — unchanged */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-slate-500 hover:text-[#2E3A59]"
              data-testid="onboarding-skip-btn"
            >
              Skip intro
            </Button>
            
            {step < totalSteps - 1 ? (
              <Button
                onClick={handleNext}
                className="bg-[#2E3A59] hover:bg-[#1A2236] btn-press"
                data-testid="onboarding-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                className="bg-[#C9A227] hover:bg-[#A6851D] text-[#1A2236] font-bold btn-press"
                data-testid="onboarding-finish-btn"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Start Practicing
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;